/**
 * Pure numeric shaping for a mesh's fade-out — strokes wiped from the board and nodes removed
 * in a shape reset both use this, rather than blinking out of existence the instant server truth
 * says they are gone. Kept separate from `RoomView`'s own Babylon plumbing (the mesh, the
 * material, the render loop) the same way `AssistantLightAnimation` is kept separate from the
 * light it drives — a plain function of numbers is what this project's test suite can actually
 * exercise directly.
 *
 * The mutation itself is never delayed by this: `RoomState` has already dropped the item by the
 * time anything here runs (CLAUDE.md, "Animate toward server truth; never delay the mutation" —
 * this file's own use of that rule, from plan/ for this milestone). All this shapes is how long
 * the mesh *lingers* on its way out, which nothing else may depend on.
 */

/** One step of a fade. */
export interface FadeStep {
  /** The alpha to paint this instant. */
  readonly alpha: number;
  /** Whether the fade has reached zero and the mesh is ready to be disposed. */
  readonly done: boolean;
}

/** Smoothstep — the same easing `AccentColorAnimation` uses, so every eased transition in this
 *  project reads the same way rather than each picking its own curve. */
function ease(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
}

/**
 * The alpha to show `elapsedMs` after a fade was queued, given a `delayMs` stagger before this
 * particular item starts moving and a `durationMs`-long fade once it does. Held at `startAlpha`
 * for the first `delayMs` — the stagger reads as a ripple across several items fading in close
 * succession, not as any single item taking longer to leave.
 */
export function fadeAlphaAt(
  elapsedMs: number,
  delayMs: number,
  durationMs: number,
  startAlpha: number,
): FadeStep {
  if (elapsedMs < delayMs) {
    return { alpha: startAlpha, done: false };
  }

  const t = (elapsedMs - delayMs) / durationMs;

  if (t >= 1) {
    return { alpha: 0, done: true };
  }

  return { alpha: startAlpha * (1 - ease(t)), done: false };
}

/**
 * The stagger delay for the next item queued into a fade-out batch — a short ripple across
 * several items fading in close succession reads better than every one moving in lockstep (the
 * owner's own framing, for the whiteboard wipe specifically, but the same reasoning applies
 * anywhere several items are removed together).
 *
 * `sinceLastMs` — elapsed time since the previous item in this batch was queued — resets the
 * stagger back to the start of the ripple once nothing has been queued for a while: a single
 * node deleted long after the last bulk wipe should fade on its own terms, not inherit a stale
 * offset from a ripple that finished long ago.
 */
export function nextFadeStagger(
  sinceLastMs: number,
  previousIndex: number,
  stepMs: number,
  maxMs: number,
  resetGapMs: number,
): { readonly delayMs: number; readonly index: number } {
  const index = sinceLastMs > resetGapMs ? 0 : previousIndex + 1;
  return { delayMs: Math.min(index * stepMs, maxMs), index };
}
